<template>
  <div
    class="relative base-input"
    :class="{
      error: hasErrors,
      disabled,
      inputVisible: (hasFocus || value !== '') && !isSearch,
      'inputVisible-search': !isMenuSearch && (hasFocus || value !== '') && isSearch,
      'mb15': !isMenuSearch
    }"
  >
    <i
      v-if="isSearch"
      class="icon icon-search absolute top-0 left-0"
      :class="[
        isMenuSearch ? 'c-black fs20 py13 pl22' : 'c-white fs15 pl23 pt19 pb15 search-input-form-icon py29-desktop',
        !isMenuSearch ? (searchFocus ? 'search-input-form-icon-active pl20-desktop pt30-desktop' : 'pl29-desktop') : '',
        {'py19': isMenuSearch && searchFocus}
      ]"
    />
    <label
      class="inline-block"
      :for="inputId"
    >
      <span class="inline-block">
        {{ placeholder }}
      </span>
      <span
        v-if="required"
        class="c-primary inline-block"
      >
        *
      </span>
    </label>
    <input
      :id="inputId"
      :ref="name"
      class="form-input"
      :class="[
        isSearch ? (isMenuSearch ? (searchFocus ? 'menu-search-input-form-active pl51' : 'pl55') : 'search-input-form pl50 brdr-none pl89-desktop') : '',
        {empty: value === '', error: hasErrors, disabled, 'menu-search-input-form': isSearch && isMenuSearch, 'search-input-form-active': isSearch && !isMenuSearch && searchFocus}
      ]"
      :type="type"
      :inputmode="inputmode"
      :name="name"
      :maxlength="maxlength"
      :autocomplete="autocomplete"
      :value="value"
      :autofocus="autofocus"
      :placeholder="placeholder"
      :readonly="readonly"
      :disabled="disabled"
      @input="onInput"
      @focus="onFocus"
      @blur="onBlur"
      @keyup.enter="$emit('keyup.enter', $event.target.value)"
      @keyup="$emit('keyup', $event)"
    >
    <span
      v-if="suggestion || reserveSuggestionSpace"
      class="lh16 flex middle-xs mt10 suggestion-slot"
    >
      <button
        v-if="suggestion"
        type="button"
        class="suggestion-text fs16 regular italic ph-no-capture"
        data-clarity-mask="true"
        @mousedown.prevent
        @click="$emit('suggestion-accept', suggestion)"
      >
        {{ $t('Did you mean {name}?', { name: suggestion }) }}
      </button>
    </span>
    <validation-messages
      v-if="validations"
      :show-text="!hasFocus"
      :validations="validations"
    />
  </div>
</template>

<script>
import ValidationMessages from './ValidationMessages.vue'
import { mapState } from 'vuex'

export default {
  name: 'BaseInput',
  components: {
    ValidationMessages
  },
  props: {
    maxlength: {
      type: [String, Number],
      required: false,
      default: null
    },
    type: {
      type: String,
      default: 'text'
    },
    inputmode: {
      type: String,
      required: false,
      default: null
    },
    sanitize: {
      type: Function,
      required: false,
      default: null
    },
    errorsOnBlurOnly: {
      type: Boolean,
      default: false
    },
    value: {
      type: [String, Number],
      default: ''
    },
    name: {
      type: String,
      required: false,
      default: ''
    },
    id: {
      type: String,
      default: null
    },
    label: {
      type: String,
      required: false,
      default: ''
    },
    placeholder: {
      type: String,
      required: false,
      default: ''
    },
    autocomplete: {
      type: String,
      required: false,
      default: ''
    },
    focus: {
      type: Boolean,
      required: false,
      default: false
    },
    required: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      required: false,
      default: false
    },
    readonly: {
      type: Boolean,
      required: false,
      default: false
    },
    autofocus: {
      type: Boolean,
      required: false,
      default: false
    },
    validations: {
      type: Array,
      default: () => []
    },
    suggestion: {
      type: String,
      default: null
    },
    reserveSuggestionSpace: {
      type: Boolean,
      default: false
    },
    isSearch: {
      type: Boolean,
      default: false
    },
    isMenuSearch: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      hasFocus: false
    }
  },
  computed: {
    inputId () {
      return this.id || `input_${this._uid}`
    },
    hasErrors () {
      if (this.errorsOnBlurOnly && this.hasFocus) {
        return false
      }

      if (this.validations && this.validations.length > 0) {
        return this.validations.some(validation => !!validation.condition)
      }

      return false
    },
    ...mapState({
      searchFocus: state => state.ui.searchSuggestions
    })
  },
  mounted () {
    if (this.focus) {
      this.$refs[this.name].focus()
    }
  },
  methods: {
    // setFocus sets focus on a field which has a value of 'ref' tag equal to fieldName
    setFocus (fieldName = this.name) {
      if (this.name === fieldName) {
        this.$refs[this.name].focus()
      }
    },
    onInput (event) {
      const typed = event.target.value
      const value = this.sanitize ? this.sanitize(typed) : typed

      if (typed !== value) {
        // selectionStart throws on input types that do not support selection (number, email)
        let caret = null

        try {
          caret = event.target.selectionStart
        } catch (error) {
          caret = null
        }

        event.target.value = value

        if (caret !== null) {
          const offset = this.caretAfterSanitize(typed, value, caret)

          try {
            event.target.setSelectionRange(offset, offset)
          } catch (error) {}
        }
      }

      this.$emit('input', value)
    },
    caretAfterSanitize (typed, value, caret) {
      let kept = 0

      for (let index = 0; index < caret && kept < value.length; index++) {
        if (typed.charAt(index) === value.charAt(kept)) {
          kept++
        }
      }

      return kept
    },
    onFocus () {
      this.$emit('focus')
      this.hasFocus = true
      if (this.isSearch) {
        this.$store.commit('ui/setSearchSuggestions', true)
      }
    },
    onBlur () {
      this.$emit('blur')
      this.hasFocus = false
      if (this.isSearch && this.value === '') {
        this.$store.commit('ui/setSearchSuggestions', false)
      }
    },
    setBlur () {
      this.$refs[this.name].blur()
    }
  }
}
</script>

<style lang="scss" scoped>
@import '~theme/css/variables/colors';
@import '~theme/css/helpers/functions/color';

.suggestion-slot {
  min-height: 24px;
}

.suggestion-text {
  padding: 0;
  border: none;
  background: none;
  color: color(black);
  text-decoration: underline;
  cursor: pointer;
}
</style>
